import { useMemo } from 'react'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { MoreVertIcon, MotionPlayIcon, TabDuplicateIcon, ModeOffOnIcon, StacksOutlineIcon, GraphIcon, DeleteIcon, TextCompareIcon, EditSquareIcon, ProjectIcon } from '@/components/icons'
import {
 DropdownMenu,
 DropdownMenuContent,
 DropdownMenuItem,
 DropdownMenuSeparator,
 DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useRoles, useDefinitionCounts } from '@/hooks'
import type { WorkflowDefinitionSummary, LinkedEntityScope } from '@/types'

const SCOPE_ICON_MAP: Record<LinkedEntityScope, React.ComponentType<{ className?: string }>> = {
 WorkItem: TextCompareIcon,
 Intent: EditSquareIcon,
 Project: ProjectIcon,
}

interface WorkflowDefinitionCardProps {
 definition: WorkflowDefinitionSummary
 canDuplicate: boolean
 canToggle: boolean
 canTrigger: boolean
 canDelete: boolean
 onNavigate: (def: WorkflowDefinitionSummary) => void
 onDuplicate: (def: WorkflowDefinitionSummary) => void
 onToggleActive: (def: WorkflowDefinitionSummary) => void
 onTrigger: (def: WorkflowDefinitionSummary) => void
 onDelete: (def: WorkflowDefinitionSummary) => void
}

export function WorkflowDefinitionCard({
 definition,
 canDuplicate,
 canToggle,
 canTrigger,
 canDelete,
 onNavigate,
 onDuplicate,
 onToggleActive,
 onTrigger,
 onDelete,
}: WorkflowDefinitionCardProps) {
 const { data: roles } = useRoles()

 const counts = useDefinitionCounts(definition.yamlContent)

 const roleLabel = useMemo(() => {
  if (!roles || definition.permittedRoleIds.length === 0) return null
  const nameById = new Map(roles.map((r) => [r.id, r.name]))
  const names = definition.permittedRoleIds.map((id) => nameById.get(id)).filter((n): n is string => !!n)
  if (names.length === 0) return null
  if (names.length <= 2) return names.join(', ')
  return `${names[0]}, ${names[1]} +${names.length - 2}`
 }, [roles, definition.permittedRoleIds])

 const ScopeIcon = definition.linkedEntityScope ? SCOPE_ICON_MAP[definition.linkedEntityScope] : null

 return (
  <Card
   className="group flex cursor-pointer flex-col transition-colors hover:bg-muted has-[[data-state=open]]:bg-muted relative rounded-2xl bg-card p-4 gap-6 border-border"
   data-testid={`workflow-definition-card-${definition.id}`}
   role="button"
   tabIndex={0}
   onClick={() => onNavigate(definition)}
   onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
     e.preventDefault()
     onNavigate(definition)
    }
   }}
  >
   <div className="absolute top-4 right-4">
    <DropdownMenu>
     <DropdownMenuTrigger asChild>
      <button
       className="shrink-0 text-foreground focus:outline-none p-2 rounded-lg hover:bg-muted data-[state=open]:bg-muted"
       aria-label={`More options for ${definition.displayName}`}
       data-testid={`workflow-menu-trigger-${definition.id}`}
       onClick={(e) => e.stopPropagation()}
      >
       <MoreVertIcon className="h-5 w-5" />
      </button>
     </DropdownMenuTrigger>
     <DropdownMenuContent
      side="bottom"
      align="end"
      sideOffset={4}
      onClick={(e) => e.stopPropagation()}
      className="border-none shadow-elevated rounded-lg py-2 px-0 bg-background gap-0.5 min-w-0"
     >
      {canDuplicate && (
       <DropdownMenuItem
        onClick={() => onDuplicate(definition)}
        className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
       >
        <TabDuplicateIcon className="h-5 w-5" />
        Duplicate
       </DropdownMenuItem>
      )}
      {canDelete && !definition.isSystem && !definition.isActive && (
       <>
        <DropdownMenuSeparator />
        <DropdownMenuItem
         onClick={() => onDelete(definition)}
         className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
        >
         <DeleteIcon className="h-5 w-5" />
         Delete
        </DropdownMenuItem>
       </>
      )}
      {canToggle && (
       <>
        <DropdownMenuSeparator />
        <DropdownMenuItem
         onClick={() => onToggleActive(definition)}
         className="h-10 px-4 text-foreground focus:text-foreground cursor-pointer hover:bg-muted font-bold leading-[1.6] tracking-[0.015em] [&_svg]:size-5"
        >
         <ModeOffOnIcon className="h-5 w-5" />
         {definition.isActive ? 'Deactivate' : 'Activate'}
        </DropdownMenuItem>
       </>
      )}
     </DropdownMenuContent>
    </DropdownMenu>
   </div>

      <CardHeader className="flex-1 p-0 pr-16">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            {ScopeIcon && (
              <div className="w-6 h-6 rounded-full bg-accent-soft text-background flex items-center justify-center shrink-0">
                <ScopeIcon className="h-4 w-4" />
              </div>
            )}
            <CardTitle className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-card-foreground break-all">{definition.displayName}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
              V{definition.version}
            </Badge>
            <Badge
              variant="outline"
              className={cn(
                'text-xs font-bold leading-[1.6] tracking-[0.015em] rounded-full px-2.5',
                definition.isActive
                  ? 'h-6 bg-active-badge-bg text-active-badge-fg border-active-badge-fg hover:bg-active-badge-bg'
                  : 'h-6 bg-inactive-badge-bg text-inactive-badge-fg border-inactive-badge-fg hover:bg-inactive-badge-bg',
              )}
            >
              {definition.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Badge variant="outline" className="text-xs" style={{ borderColor: 'rgba(118,124,150,1)' }}>
              {definition.isSystem ? 'System' : 'Custom'}
            </Badge>
          </div>
        </div>
      </CardHeader>

   <div>
    {definition.description && (
     <TooltipProvider>
      <Tooltip>
       <TooltipTrigger asChild>
        <p className="text-sm font-normal leading-[1.4] tracking-[0.015em] text-card-foreground/72 line-clamp-2">
         {definition.description}
        </p>
       </TooltipTrigger>
       <TooltipContent side="bottom" className="max-w-xs">
        <p>{definition.description}</p>
       </TooltipContent>
      </Tooltip>
     </TooltipProvider>
    )}
   </div>

   <div className="flex items-end justify-between">
    <div className="flex items-center gap-5 text-[12px] font-normal leading-[1.6] tracking-[0.015em] text-card-foreground">
     {counts ? (
      <>
       <span className="flex items-center gap-1.5">
        <StacksOutlineIcon className="h-4 w-4" />
        Phases: {counts.phases}
       </span>
       <span className="flex items-center gap-1.5">
        <GraphIcon className="h-4 w-4" />
        Nodes: {counts.nodes}
       </span>
      </>
     ) : (
      `v${definition.version}`
     )}
     {roleLabel && (
      <span data-testid={`workflow-definition-card-roles-${definition.id}`}>
       Role: {roleLabel}
      </span>
     )}
    </div>
    {canTrigger && (
     <Button
      className="h-10 min-w-10 rounded-lg px-4 py-2.5 gap-2.5"
      disabled={!definition.isActive}
      onClick={(e) => {
       e.stopPropagation()
       onTrigger(definition)
      }}
     >
      <MotionPlayIcon className="h-5 w-5" />
      Trigger
     </Button>
    )}
   </div>
  </Card>
 )
}
